/**
 * Property Form State, Validation, and Mappers
 *
 * Pure, testable module extracted from the property create/edit pages.
 * Contains: FormState type, INITIAL_FORM, helper predicates, validateForm,
 * formStateToPayload, and propertyToFormState.
 *
 * No React imports — safe to use in tests and non-React contexts.
 */

import { AMENITIES_LIST } from '../config/propertyFormOptions';
import type { HouseStyle } from '../config/propertyFormOptions';
import { getPropertyTypeConfig, getPropertyCategory, getAllowedAmenities, getVisibleFieldGroups } from '../config/propertyTypeConfig';
import type { Property } from '../mocks/properties';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ListingType = 'sale' | 'rent';
export type PropertyCategory = 'residential' | 'commercial';
export type FurnishingStatus = 'furnished' | 'unfurnished' | 'partly_furnished';

import type { PropertyCondition } from '../config/propertyFormOptions';
export type { PropertyCondition };

export interface FormState {
  // Basic info
  title: string;
  description: string;
  category: PropertyCategory;
  propertyType: string;
  listingType: ListingType;
  // 'me' = personal listing (companyId NULL — broker keeps it on company exit).
  // 'company' = published under the company's FAL. Only relevant for brokers
  // who are currently a team member; defaults to 'company' for members and
  // is hidden from the UI for independent brokers.
  listUnder: 'me' | 'company';

  // Pricing
  price: string;
  // Rent period (سنوي/شهري/كل ثلاثة أشهر). Persisted as Property.rentPeriod and
  // used by the backend to normalize rent to monthly. Sale listings ignore it.
  pricePeriod: 'monthly' | 'yearly' | 'quarterly';

  // Specs
  bedrooms: string;
  bathrooms: string;
  livingRooms: string;
  kitchens: string;
  sqft: string;
  floor: string;
  totalFloors: string;
  yearBuilt: string;
  parkingSpaces: string;
  propertyCondition: PropertyCondition;

  // Furnishing
  furnishingStatus: FurnishingStatus;

  // Residential house attachment style (detached / semi-detached / terraced).
  houseStyle: HouseStyle;

  // Location
  streetAddress: string;
  buildingNumber: string;
  unitNumber: string;
  city: string;
  region: string;
  district: string;
  postalCode: string;
  additionalCode: string;
  latitude: string;
  longitude: string;

  // Building specs
  totalUnits: string;

  // Features
  amenities: string[];
  utilities: string[];

  // Media
  imageUrl: string;
  galleryUrls: string;
  videoTourUrl: string;
  virtualTourUrl: string;

  // Contact
  agentName: string;
  agentPhone: string;
  agentEmail: string;
  agentWhatsapp: string;

  // REGA / documents & license (all optional except the two license numbers,
  // which are required for broker/edit variants — enforced in validateForm).
  residentialType: '' | 'families' | 'singles';
  orientation: string; // backend Orientation enum value, or ''
  deedStatus: string; // backend DeedStatus enum value, or ''
  constructionStatus: string; // backend ConstructionStatus enum value, or ''
  // Construction progress (shown only when constructionStatus !== READY).
  constructionStage: string; // ConstructionStage enum value, or ''
  constructionProgress: string; // 0–100
  constructionStartDate: string; // YYYY-MM-DD
  expectedCompletionDate: string; // YYYY-MM-DD
  ownerType: string; // backend OwnerType enum value, or ''
  propertyAge: string;
  streetWidth: string;
  planNumber: string;
  landNumber: string;
  deedNumber: string;
  warranty: string;
  notes: string;
  adLicenseNumber: string;
  falLicenseNumber: string;
  licenseIssueDate: string; // YYYY-MM-DD
  licenseExpiryDate: string; // YYYY-MM-DD
  adChannels: string[];
  encumbrance: boolean;
  saudiBuildingCodeCompliant: boolean;
  mortgaged: boolean;
  restricted: boolean;

  // Status
  status: Property['status'];

  // ---- Phase 4 property intake v2 (additive). The UI that sets these is gated
  // by VITE_PROPERTY_INTAKE_V2_ENABLED; the fields themselves always round-trip
  // through the payload + edit prefill so an edit never clobbers a saved value.
  // `intakeCategory` is the backend propertyCategory (RESIDENTIAL|COMMERCIAL|MIXED)
  // — distinct from the UI-only `category` toggle above.
  intakeCategory: '' | 'RESIDENTIAL' | 'COMMERCIAL' | 'MIXED';
  flatsCount: string;
  shopsCount: string;
  facadeDirection: '' | 'NORTH' | 'SOUTH' | 'EAST' | 'WEST' | 'CORNER';
  displayVisibility: 'PUBLIC' | 'PRIVATE';
  // Multi-select offerings (subset of SALE|RENT|SHORT_RENT|SEEKING_INVESTMENT|
  // SEEKING_DEVELOPER). Property.listingType stays the PRIMARY offering.
  offeringTypes: string[];
}

// ---------------------------------------------------------------------------
// Initial form state
// ---------------------------------------------------------------------------

const thisYear = new Date().getFullYear();

export const INITIAL_FORM: FormState = {
  title: '',
  description: '',
  category: 'residential',
  propertyType: 'apartment',
  listingType: 'sale',
  listUnder: 'company', // default for members; non-members never see the field anyway

  price: '',
  pricePeriod: 'yearly',
  bedrooms: '',
  bathrooms: '',
  livingRooms: '',
  kitchens: '',
  sqft: '',
  floor: '',
  totalFloors: '',
  yearBuilt: `${thisYear}`,
  parkingSpaces: '',
  propertyCondition: '',
  furnishingStatus: 'unfurnished',
  houseStyle: '',
  totalUnits: '',
  streetAddress: '',
  buildingNumber: '',
  unitNumber: '',
  city: '',
  region: '',
  district: '',
  postalCode: '',
  additionalCode: '',
  latitude: '',
  longitude: '',
  amenities: [],
  utilities: [],
  imageUrl: '',
  galleryUrls: '',
  videoTourUrl: '',
  virtualTourUrl: '',
  agentName: '',
  agentPhone: '',
  agentEmail: '',
  agentWhatsapp: '',
  residentialType: '',
  orientation: '',
  deedStatus: '',
  constructionStatus: '',
  constructionStage: '',
  constructionProgress: '',
  constructionStartDate: '',
  expectedCompletionDate: '',
  ownerType: '',
  propertyAge: '',
  streetWidth: '',
  planNumber: '',
  landNumber: '',
  deedNumber: '',
  warranty: '',
  notes: '',
  adLicenseNumber: '',
  falLicenseNumber: '',
  licenseIssueDate: '',
  licenseExpiryDate: '',
  adChannels: [],
  encumbrance: false,
  saudiBuildingCodeCompliant: false,
  mortgaged: false,
  restricted: false,
  status: 'available',

  // Phase 4 intake v2 — defaults match prior behaviour (PUBLIC, nothing set).
  intakeCategory: '',
  flatsCount: '',
  shopsCount: '',
  facadeDirection: '',
  displayVisibility: 'PUBLIC',
  offeringTypes: [],
};

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export function numberFromInput(value: string): number {
  const cleaned = value.replace(/[^0-9.-]/g, '');
  return cleaned ? Number(cleaned) : NaN;
}

export function parseList(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

/**
 * Toggle `id` in a multi-select `list`, honouring mutual-exclusion groups: when
 * an id is being ADDED, any other id sharing one of its mutex groups is removed
 * first. Used for utilities (standard vs. fibre internet are one connection).
 */
export function toggleInList(list: string[], id: string, mutexGroups: string[][] = []): string[] {
  if (list.includes(id)) return list.filter((x) => x !== id);
  const conflicts = new Set(
    mutexGroups.filter((g) => g.includes(id)).flat().filter((x) => x !== id),
  );
  return [...list.filter((x) => !conflicts.has(x)), id];
}

// Map web form property types to backend format
// IMPORTANT: Preserve exact types to avoid data loss. Send the actual type in uppercase.
export function mapPropertyType(type: string): string {
  return type.toUpperCase().replace(/-/g, '_');
}

// ---------------------------------------------------------------------------
// Property-type predicate helpers
// ---------------------------------------------------------------------------

// Helper to check if property type needs room/bath fields (uses config)
export const needsRoomFields = (type: string): boolean => {
  const config = getPropertyTypeConfig(type.toUpperCase());
  // Check if bedrooms attribute is configured to show for this property type
  return config.attributes.bedrooms?.show !== false;
};

// Helper to check if property type needs furnishing field (uses config)
export const needsFurnishingField = (type: string): boolean => {
  const config = getPropertyTypeConfig(type.toUpperCase());
  return config.attributes.furnished?.show !== false;
};

// Helper to check if property type needs floor field
export const needsFloorField = (type: string): boolean => {
  return ['apartment', 'floor', 'office', 'penthouse', 'duplex', 'shop', 'room', 'showroom', 'studio'].includes(type);
};

// Helper to check if property type is building (no individual pricing)
export const isBuilding = (type: string): boolean => {
  return ['building', 'commercial_building'].includes(type);
};

// Helper to check if property type is a land category
export const isLand = (type: string): boolean => {
  return getPropertyCategory(type.toUpperCase()) === 'land';
};

// Helper to check if amenities are required for property type
export const requiresAmenities = (type: string): boolean => {
  const config = getPropertyTypeConfig(type.toUpperCase());
  // Land types don't need amenities
  return config.category !== 'land';
};

/**
 * Returns a new FormState with any amenities that are not allowed for the
 * current property type removed. Returns the same object reference if no
 * pruning was needed (safe for React state comparisons).
 */
export function pruneAmenitiesForType(form: FormState): FormState {
  // Pass listingType so pet_friendly is dropped on sale listings, and 'parking'
  // (no longer an amenity) is pruned from any legacy prefill.
  const allowed = new Set(getAllowedAmenities(form.propertyType, form.listingType));
  const amenities = form.amenities.filter((a) => allowed.has(a));
  return amenities.length === form.amenities.length ? form : { ...form, amenities };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Validates the form state and returns a map of field → error message.
 * An empty map means the form is valid.
 *
 * @param form  - The current form state
 * @param opts  - Options: lang ('en' | 'ar') determines which language to use
 *                for error messages (replaces the old isRTL closure dependency)
 */
export function validateForm(
  form: FormState,
  opts: { lang: 'en' | 'ar'; variant?: 'broker' | 'individual' | 'edit' },
): Record<string, string> {
  const messages: Record<string, string> = {};
  const rtl = opts.lang === 'ar';
  // Broker/company listings carry their own REGA advertising licence; the two
  // licence numbers are mandatory for them. Individual platform-managed FAL
  // listings get the licence attached by the platform, so they stay optional.
  const requireLicense = opts.variant === 'broker' || opts.variant === 'edit';

  const price = numberFromInput(form.price);
  const groups = getVisibleFieldGroups(form.propertyType);
  const showPricing = groups.price;

  // Title: required, min 3 chars (backend requirement)
  if (!form.title.trim() || form.title.trim().length < 3) {
    messages.title = rtl ? 'العنوان مطلوب (3 أحرف على الأقل)' : 'Title is required (min 3 characters)';
  }

  // Price: required and positive for all types except buildings (land has a sale price)
  if (showPricing && (!Number.isFinite(price) || price <= 0)) {
    messages.price = rtl ? 'السعر غير صالح' : 'Invalid price';
  }

  // Area: required and positive integer
  const sqft = numberFromInput(form.sqft);
  if (!Number.isFinite(sqft) || sqft <= 0) {
    messages.sqft = rtl ? 'المساحة غير صالحة' : 'Invalid area';
  }

  // Bedrooms/Bathrooms: validate only for types that show them (empty = 0 = valid; only negative is invalid)
  if (groups.bedrooms) {
    const bedrooms = numberFromInput(form.bedrooms);
    if (form.bedrooms.trim() !== '' && (!Number.isFinite(bedrooms) || bedrooms < 0)) {
      messages.bedrooms = rtl ? 'عدد غرف النوم غير صالح' : 'Invalid bedrooms';
    }
  }
  if (groups.bathrooms) {
    const bathrooms = numberFromInput(form.bathrooms);
    if (form.bathrooms.trim() !== '' && (!Number.isFinite(bathrooms) || bathrooms < 0)) {
      messages.bathrooms = rtl ? 'عدد دورات المياه غير صالح' : 'Invalid bathrooms';
    }
  }

  // Description: required, min 10 chars (backend requirement)
  if (!form.description.trim() || form.description.trim().length < 10) {
    messages.description = rtl ? 'الوصف مطلوب (10 أحرف على الأقل)' : 'Description is required (min 10 characters)';
  }

  // City: required
  if (!form.city.trim()) {
    messages.city = rtl ? 'المدينة مطلوبة' : 'City is required';
  }

  // Lat/Lng: required (but can be entered manually)
  const lat = parseFloat(form.latitude);
  const lng = parseFloat(form.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    messages.latitude = rtl ? 'يرجى إدخال الإحداثيات أو اختيار عنوان' : 'Please enter coordinates or select an address';
  }

  // Agent name: optional per backend but let's keep for UX
  if (!form.agentName.trim() || form.agentName.trim().length < 2) {
    messages.agentName = rtl ? 'اسم الوكيل مطلوب (حرفان على الأقل)' : 'Agent name required (min 2 characters)';
  }
  if (!form.agentPhone.trim()) {
    messages.agentPhone = rtl ? 'رقم الهاتف مطلوب' : 'Phone is required';
  }
  if (!form.agentEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.agentEmail.trim())) {
    messages.agentEmail = rtl ? 'البريد الإلكتروني غير صالح' : 'Invalid email';
  }

  // Amenities: required only for non-land property types
  if (requiresAmenities(form.propertyType) && form.amenities.length === 0) {
    messages.amenities = rtl ? 'يرجى اختيار ميزة واحدة على الأقل' : 'Please select at least one amenity';
  }

  // REGA advertising licence: required for broker/company listings.
  if (requireLicense) {
    if (!form.adLicenseNumber.trim()) {
      messages.adLicenseNumber = rtl ? 'رقم ترخيص الإعلان مطلوب' : 'Advertisement licence number is required';
    }
    if (!form.falLicenseNumber.trim()) {
      messages.falLicenseNumber = rtl ? 'رقم رخصة فال مطلوب' : 'FAL licence number is required';
    }
  }

  return messages;
}

// ---------------------------------------------------------------------------
// Payload mapper (form → API body)
// ---------------------------------------------------------------------------

/**
 * Maps form state + uploaded image URLs into the API payload that is POSTed
 * to /properties. The shape matches the backend createPropertySchema exactly.
 *
 * @param form       - The current form state
 * @param imageUrls  - The ordered array of uploaded image URLs (cover first)
 *                     The caller is responsible for reordering so that the
 *                     cover image is at index 0, and for separating floor-plan
 *                     URLs — pass only the full ordered list here; floor plans
 *                     are passed separately via the floorPlanUrls parameter.
 * @param floorPlanUrls - Subset of imageUrls that were tagged as floor plans
 */
export function formStateToPayload(
  form: FormState,
  imageUrls: string[],
  floorPlanUrls: string[] = [],
): Record<string, unknown> {
  const groups = getVisibleFieldGroups(form.propertyType);
  const showPricing = groups.price;

  // Map amenities to API format (id → label)
  const amenitiesList = form.amenities.map((id) => {
    const amenity = AMENITIES_LIST.find((a) => a.id === id);
    return amenity?.label || id;
  });

  // Cover image is the first URL; rest go to gallery
  const finalMainImageUrl = imageUrls[0] ||
    (form.imageUrl.trim() && !form.imageUrl.startsWith('data:') ? form.imageUrl.trim() : undefined);
  const galleryUrls = imageUrls.slice(1);

  return {
    // Required fields
    title: form.title.trim(),
    price: showPricing ? numberFromInput(form.price) : 0,
    bedrooms: groups.bedrooms ? Number(numberFromInput(form.bedrooms) || 0) : 0,
    bathrooms: groups.bathrooms ? Number(numberFromInput(form.bathrooms) || 0) : 0,
    sqft: Math.round(numberFromInput(form.sqft) * 10.7639), // form input is m²; backend column is sqft
    address: form.streetAddress.trim() || form.city.trim(), // Required min 3 chars
    city: form.city.trim(),
    state: form.region.trim() || form.city.trim(), // Required - use city as fallback
    latitude: form.latitude ? parseFloat(form.latitude) : 0,
    longitude: form.longitude ? parseFloat(form.longitude) : 0,
    description: form.description.trim(),

    // Optional fields matching backend schema
    district: form.district.trim() || undefined,
    zipCode: form.postalCode.trim() || undefined,
    buildingNumber: form.buildingNumber.trim() || undefined,
    unitNumber: form.unitNumber.trim() || undefined,
    additionalCode: form.additionalCode.trim() || undefined,
    propertyType: mapPropertyType(form.propertyType),
    listingType: form.listingType.toUpperCase(),
    // Rent period — sent only for rent so a sale listing never sets a meaningless
    // period (and never clobbers one on edit; the backend keeps it when omitted).
    rentPeriod: form.listingType === 'rent' ? form.pricePeriod : undefined,
    // Backend re-validates against the caller's actual agent.companyId.
    // Sending this anyway so the explicit user choice flows through; the
    // server still falls back to safe defaults for non-members.
    listUnder: form.listUnder,
    yearBuilt: form.yearBuilt ? Number(form.yearBuilt) : undefined,
    propertyCondition: form.propertyCondition || undefined,
    // Parking is captured by the dedicated Parking Spaces field — derive the
    // boolean from it rather than a (now-removed) "parking" amenity chip.
    parking: numberFromInput(form.parkingSpaces) > 0,
    petFriendly: form.amenities.includes('pet_friendly'),
    furnished: form.furnishingStatus === 'furnished',
    furnishingStatus: form.furnishingStatus || undefined,
    amenities: amenitiesList,
    utilities: form.utilities.length > 0 ? form.utilities : undefined,
    livingRooms: groups.livingRooms && form.livingRooms ? Number(form.livingRooms) : undefined,
    kitchens: groups.kitchens && form.kitchens ? Number(form.kitchens) : undefined,
    // Residential house attachment style — only sent when the type shows it.
    houseStyle: groups.attachment && form.houseStyle ? form.houseStyle : undefined,
    totalUnits: form.totalUnits ? numberFromInput(form.totalUnits) : undefined,

    // Agent info
    agentName: form.agentName.trim() || undefined,
    agentPhone: form.agentPhone.trim() || undefined,
    agentEmail: form.agentEmail.trim() || undefined,
    agentWhatsapp: form.agentWhatsapp.trim() || undefined,

    // Images - use uploaded URLs from Cloudinary
    imageUrl: finalMainImageUrl,
    galleryImages: galleryUrls.length > 0 ? galleryUrls : undefined,
    // Floor-plan tags (G-Task 6): subset of image URLs the broker marked as floor plans
    floorPlanUrls: floorPlanUrls.length > 0 ? floorPlanUrls : [],

    // Video/Virtual tours
    videoUrl: form.videoTourUrl.trim() || undefined,
    virtualTourUrl: form.virtualTourUrl.trim() || undefined,

    // REGA / documents & license. Enum/string columns + metadata fields.
    // Send `|| undefined` for text so empties don't clobber on edit; booleans
    // are always sent (the toggles have a definite true/false state).
    residentialType: form.residentialType || undefined,
    orientation: form.orientation || undefined,
    deedStatus: form.deedStatus || undefined,
    constructionStatus: form.constructionStatus || undefined,
    constructionStage: form.constructionStage || undefined,
    constructionProgress: form.constructionProgress.trim() ? Number(numberFromInput(form.constructionProgress)) : undefined,
    constructionStartDate: form.constructionStartDate.trim() || undefined,
    expectedCompletionDate: form.expectedCompletionDate.trim() || undefined,
    ownerType: form.ownerType || undefined,
    propertyAge: form.propertyAge.trim() ? Number(numberFromInput(form.propertyAge)) : undefined,
    streetWidth: form.streetWidth.trim() ? Number(numberFromInput(form.streetWidth)) : undefined,
    planNumber: form.planNumber.trim() || undefined,
    landNumber: form.landNumber.trim() || undefined,
    deedNumber: form.deedNumber.trim() || undefined,
    warranty: form.warranty.trim() || undefined,
    notes: form.notes.trim() || undefined,
    adLicenseNumber: form.adLicenseNumber.trim() || undefined,
    falLicenseNumber: form.falLicenseNumber.trim() || undefined,
    licenseIssueDate: form.licenseIssueDate.trim() || undefined,
    licenseExpiryDate: form.licenseExpiryDate.trim() || undefined,
    adChannels: form.adChannels.length > 0 ? form.adChannels : undefined,
    encumbrance: form.encumbrance,
    saudiBuildingCodeCompliant: form.saudiBuildingCodeCompliant,
    mortgaged: form.mortgaged,
    restricted: form.restricted,

    // Listing status — owner-editable (available / under-negotiation / rented /
    // sold). Defaults to 'available' for new listings via INITIAL_FORM.
    status: form.status || 'available',

    // Phase 4 intake v2 (additive). Scalars are sent only when set so an edit
    // never clobbers them with a blank; displayVisibility always round-trips
    // (its 'PUBLIC' default equals the backend default, and propertyToFormState
    // reads the saved value back so a PRIVATE listing stays PRIVATE on edit).
    propertyCategory: form.intakeCategory || undefined,
    flatsCount: form.flatsCount.trim() ? Number(numberFromInput(form.flatsCount)) : undefined,
    shopsCount: form.shopsCount.trim() ? Number(numberFromInput(form.shopsCount)) : undefined,
    facadeDirection: form.facadeDirection || undefined,
    displayVisibility: form.displayVisibility || 'PUBLIC',
    offeringTypes: form.offeringTypes.length > 0 ? form.offeringTypes : undefined,
  };
}

// ---------------------------------------------------------------------------
// Server error → form field mapper
// ---------------------------------------------------------------------------

/**
 * Maps backend validation errors (zod issue shape) to form-state field keys.
 *
 * The backend returns errors as:
 *   { message: string, details: Array<{ path: (string|number)[], message: string }> }
 *
 * Backend field names that differ from form keys are translated via SERVER_TO_FORM_FIELD.
 * If no details are present, the top-level message falls back to _form.
 */
const SERVER_TO_FORM_FIELD: Record<string, string> = {
  address: 'streetAddress',
  state: 'region',
  galleryImages: 'imageUrl',
  // identity-mapped keys (title, price, city, latitude, longitude, description,
  // sqft, bedrooms, bathrooms, agentName, agentPhone, agentEmail) need no entry
};

export function mapServerErrorToFields(
  err: { message?: string; details?: Array<{ path?: (string | number)[]; message: string }> }
): Record<string, string> {
  const out: Record<string, string> = {};
  if (Array.isArray(err?.details) && err.details.length) {
    for (const issue of err.details) {
      const key = String(issue.path?.[0] ?? '');
      const formKey = SERVER_TO_FORM_FIELD[key] ?? (key || '_form');
      out[formKey] = issue.message;
    }
  }
  if (Object.keys(out).length === 0 && err?.message) out._form = err.message;
  return out;
}

// ---------------------------------------------------------------------------
// Inverse mapper (API property record → form state)
// ---------------------------------------------------------------------------

/**
 * Reads the first defined value among the given keys on a record. Lets the
 * edit-page mappers accept both the web API shape (camelCase) and the mobile
 * API shape (snake_case) for the same logical field.
 */
function pick(p: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    if (p[k] != null) return p[k];
  }
  return undefined;
}

const strOr = (v: unknown, fallback = ''): string =>
  typeof v === 'string' ? v : v != null ? String(v) : fallback;

/**
 * Reads Phase-4 multi-select offering types back from an API property record.
 * Accepts a flat `offeringTypes: string[]` or a relation
 * `offerings: [{ offeringType }]` (or mobile `{ type }`); returns [] otherwise.
 */
function extractOfferingTypes(p: Record<string, unknown>): string[] {
  const direct = p.offeringTypes;
  if (Array.isArray(direct)) return direct.map((x) => String(x).toUpperCase()).filter(Boolean);
  const offerings = p.offerings;
  if (Array.isArray(offerings)) {
    return offerings
      .map((o) => {
        if (typeof o === 'string') return o.toUpperCase();
        if (o && typeof o === 'object') {
          const rec = o as Record<string, unknown>;
          const v = rec.offeringType ?? rec.type;
          return v != null ? String(v).toUpperCase() : '';
        }
        return '';
      })
      .filter(Boolean);
  }
  return [];
}

/**
 * Converts a property record from the API into editable form state.
 * Used by the edit page variant. Accepts both the web (camelCase) and the
 * mobile (snake_case) API response shapes for each field, mirroring the
 * fallbacks the old edit page handled inline. The area conversion (DB sqft →
 * form m²) is applied here so the form shows m² like the create flow.
 */
export function propertyToFormState(p: Record<string, unknown>): FormState {
  const labelToId = new Map(AMENITIES_LIST.map((a) => [a.label.toLowerCase(), a.id]));

  const rawAmenities = Array.isArray(p.amenities) ? (p.amenities as string[]) : [];

  // Property type → form id (lowercased) and the derived UI category.
  const propertyType = strOr(pick(p, 'propertyType', 'type'), 'apartment').toLowerCase();
  const category = getPropertyCategory(propertyType.toUpperCase());
  // The form UI only toggles residential/commercial; everything that isn't a
  // residential/land bucket lives under the "commercial" tab there.
  const uiCategory: PropertyCategory =
    category === 'residential' || category === 'land' ? 'residential' : 'commercial';

  // form.sqft holds m² (Saudi convention); DB sqft is true sqft. Prefer an
  // explicit area_sqm if the (mobile) API supplied one, else convert from sqft.
  const areaSqm = pick(p, 'area_sqm');
  const rawSqft = pick(p, 'sqft');
  const sqft =
    areaSqm != null
      ? String(areaSqm)
      : rawSqft != null
        ? String(Math.round(Number(rawSqft) / 10.7639))
        : '';

  const rawStatus = strOr(pick(p, 'status', 'listing_status'), 'available');
  const status = (rawStatus === 'active' ? 'available' : rawStatus) as Property['status'];

  const furnished = pick(p, 'furnished');
  const parkingSpaces = pick(p, 'parking_spaces');
  const parkingValue = pick(p, 'parking');
  const hasParking = parkingValue == null && parkingSpaces == null
    ? undefined
    : (parkingValue ?? (parkingSpaces != null && Number(parkingSpaces) > 0));

  // Convert an optional numeric source to a form string field ('' when absent).
  const numStr = (...keys: string[]): string => {
    const v = pick(p, ...keys);
    return v != null ? String(v) : '';
  };

  return {
    ...INITIAL_FORM,
    title: strOr(pick(p, 'title')),
    description: strOr(pick(p, 'description')),
    category: uiCategory,
    propertyType,
    listingType: strOr(pick(p, 'listingType', 'listing_type'), 'sale').toLowerCase() as ListingType,
    price: numStr('price'),
    pricePeriod: (['monthly', 'quarterly', 'yearly'].includes(strOr(pick(p, 'rentPeriod', 'rent_period')).toLowerCase())
      ? strOr(pick(p, 'rentPeriod', 'rent_period')).toLowerCase()
      : 'yearly') as FormState['pricePeriod'],
    sqft,
    bedrooms: numStr('bedrooms', 'rooms'),
    bathrooms: numStr('bathrooms'),
    livingRooms: numStr('livingRooms', 'living_rooms'),
    kitchens: numStr('kitchens'),
    floor: numStr('floor'),
    totalFloors: numStr('totalFloors', 'total_floors'),
    city: strOr(pick(p, 'city')),
    region: strOr(pick(p, 'state', 'region')),
    district: strOr(pick(p, 'district')),
    streetAddress: strOr(pick(p, 'address', 'street_address')),
    buildingNumber: strOr(pick(p, 'buildingNumber', 'building_number')),
    unitNumber: strOr(pick(p, 'unitNumber', 'unit_number')),
    postalCode: strOr(pick(p, 'zipCode', 'postal_code')),
    additionalCode: strOr(pick(p, 'additionalCode', 'additional_code')),
    propertyCondition: (strOr(pick(p, 'propertyCondition', 'property_condition')) as PropertyCondition) || '',
    parkingSpaces: hasParking === undefined ? '' : hasParking ? '1' : '0',
    latitude: numStr('latitude'),
    longitude: numStr('longitude'),
    amenities: rawAmenities.map((a: string) => labelToId.get(String(a).toLowerCase()) ?? a),
    utilities: Array.isArray(p.utilities) ? (p.utilities as string[]) : [],
    imageUrl: strOr(pick(p, 'imageUrl', 'image_url')),
    videoTourUrl: strOr(pick(p, 'videoUrl', 'videoTourUrl', 'video_tour_url')),
    virtualTourUrl: strOr(pick(p, 'virtualTourUrl', 'virtual_tour_url')),
    agentName: strOr(pick(p, 'agentName', 'contact_name')),
    agentPhone: strOr(pick(p, 'agentPhone', 'contact_phone')),
    agentEmail: strOr(pick(p, 'agentEmail', 'contact_email')),
    agentWhatsapp: strOr(pick(p, 'agentWhatsapp', 'contact_whatsapp')),
    yearBuilt: pick(p, 'yearBuilt', 'year_built') != null ? String(pick(p, 'yearBuilt', 'year_built')) : String(new Date().getFullYear()),
    furnishingStatus: furnished === true ? 'furnished' : (p.furnishingStatus as FurnishingStatus) || 'unfurnished',
    houseStyle: (strOr(pick(p, 'houseStyle', 'house_style')) as HouseStyle) || '',
    totalUnits: numStr('totalUnits', 'total_units'),
    listUnder: (p.listUnder as 'me' | 'company') || 'company',

    // REGA / documents & license. Enums come back uppercase; dates are sliced to
    // YYYY-MM-DD for <input type="date">; booleans default to false when absent.
    residentialType: (strOr(pick(p, 'residentialType', 'residential_type')) as FormState['residentialType']) || '',
    orientation: strOr(pick(p, 'orientation')).toUpperCase(),
    deedStatus: strOr(pick(p, 'deedStatus', 'deed_status')).toUpperCase(),
    constructionStatus: strOr(pick(p, 'constructionStatus', 'construction_status')).toUpperCase(),
    constructionStage: strOr(pick(p, 'constructionStage', 'construction_stage')).toUpperCase(),
    constructionProgress: numStr('constructionProgress', 'construction_progress'),
    constructionStartDate: strOr(pick(p, 'constructionStartDate', 'construction_start_date')).slice(0, 10),
    expectedCompletionDate: strOr(pick(p, 'expectedCompletionDate', 'expected_completion_date')).slice(0, 10),
    ownerType: strOr(pick(p, 'ownerType', 'owner_type')).toUpperCase(),
    propertyAge: numStr('propertyAge', 'property_age'),
    streetWidth: numStr('streetWidth', 'street_width'),
    planNumber: strOr(pick(p, 'planNumber', 'plan_number')),
    landNumber: strOr(pick(p, 'landNumber', 'land_number')),
    deedNumber: strOr(pick(p, 'deedNumber', 'deed_number')),
    warranty: strOr(pick(p, 'warranty')),
    notes: strOr(pick(p, 'notes')),
    adLicenseNumber: strOr(pick(p, 'adLicenseNumber', 'ad_license_number')),
    falLicenseNumber: strOr(pick(p, 'falLicenseNumber', 'fal_license_number')),
    licenseIssueDate: strOr(pick(p, 'licenseIssueDate', 'license_issue_date')).slice(0, 10),
    licenseExpiryDate: strOr(pick(p, 'licenseExpiryDate', 'license_expiry_date')).slice(0, 10),
    adChannels: Array.isArray(p.adChannels) ? (p.adChannels as string[]) : [],
    encumbrance: pick(p, 'encumbrance') === true,
    saudiBuildingCodeCompliant: pick(p, 'saudiBuildingCodeCompliant', 'saudi_building_code_compliant') === true,
    mortgaged: pick(p, 'mortgaged') === true,
    restricted: pick(p, 'restricted') === true,

    // Phase 4 intake v2 — read saved values back so an edit preserves them
    // (the payload mapper would otherwise re-send defaults and clobber).
    intakeCategory: (strOr(pick(p, 'propertyCategory', 'property_category')).toUpperCase() as FormState['intakeCategory']) || '',
    flatsCount: numStr('flatsCount', 'flats_count'),
    shopsCount: numStr('shopsCount', 'shops_count'),
    facadeDirection: (strOr(pick(p, 'facadeDirection', 'facade_direction')).toUpperCase() as FormState['facadeDirection']) || '',
    displayVisibility: strOr(pick(p, 'displayVisibility', 'display_visibility')).toUpperCase() === 'PRIVATE' ? 'PRIVATE' : 'PUBLIC',
    offeringTypes: extractOfferingTypes(p),

    status,
  };
}

/**
 * Extracts the ordered list of existing gallery image URLs from an API
 * property record, with the cover/main image first (de-duplicated). Accepts
 * both web (`imageUrl`/`galleryImages`) and mobile (`image_url`/`image_urls`)
 * shapes. Pass the result to PropertyForm's `initialImageUrls`.
 */
export function propertyToImageUrls(p: Record<string, unknown>): string[] {
  const main = strOr(pick(p, 'imageUrl', 'image_url'));
  const galleryRaw = pick(p, 'galleryImages', 'image_urls');
  const gallery = Array.isArray(galleryRaw) ? (galleryRaw as string[]) : [];
  const ordered = main ? [main, ...gallery.filter((url) => url !== main)] : gallery;
  // De-dupe defensively while preserving order.
  return ordered.filter((url, i) => url && ordered.indexOf(url) === i);
}

/**
 * Extracts the set of existing floor-plan image URLs from an API property
 * record. Used by the edit page to re-tag previously-saved floor plans.
 */
export function propertyToFloorPlanUrls(p: Record<string, unknown>): string[] {
  const raw = pick(p, 'floorPlanImages', 'floorPlanUrls', 'floor_plan_images');
  return Array.isArray(raw) ? (raw as string[]).filter((u) => typeof u === 'string') : [];
}
